import React from 'react';
import {
  AudioWavesIcon,
  Edit04Icon,
  FolderPlusIcon,
  Lightbulb04Icon,
  RecordIcon,
  UploadIcon,
  VideoIcon,
} from 'assets/icons';
import { classnames } from 'helpers/utils';
import styles from './new-menu.module.scss';

interface NewMenuItem {
  label: string;
  icon: React.ReactNode;
  action?: () => void;
}

interface NewMenuSection {
  title: string;
  items: NewMenuItem[];
}

export interface NewMenuListProps {
  // The unified Upload (files and folders → review). When given it replaces
  // "Upload files" and "Bulk Upload".
  onUpload?: () => void;
  onUploadFiles?: () => void;
  onCreateNote?: () => void;
  onCreateAudio?: () => void;
  onCreateVideo?: () => void;
  // Opens the video call waiting room. Hidden when not given.
  onCreateVideoCall?: () => void;
  onCreateAlbum?: () => void;
  onBulkUpload?: () => void;
  // Opens the "add prompts" picker. "New Prompt" is hidden when not
  // given.
  onNewPrompt?: () => void;
  // Hide "New Album" (e.g. inside an album, where you only add memories to the
  // current album).
  showAlbumSection?: boolean;
  // Called before an item's action runs, so the host can close its popover.
  onClose?: () => void;
  className?: string;
}

// The dropdown contents of the "New" menu, shared by the top-bar NewMenu and
// the in-album "New" button.
export const NewMenuList = ({
  onUpload,
  onUploadFiles,
  onCreateNote,
  onCreateAudio,
  onCreateVideo,
  onCreateVideoCall,
  onCreateAlbum,
  onBulkUpload,
  onNewPrompt,
  showAlbumSection = true,
  onClose,
  className,
}: NewMenuListProps) => {
  const run = (action?: () => void) => () => {
    onClose?.();
    action?.();
  };

  const allSections: NewMenuSection[] = [
    {
      title: 'New Memory',
      items: [
        onUpload
          ? { label: 'Upload', icon: <UploadIcon />, action: onUpload }
          : {
              label: 'Upload files',
              icon: <UploadIcon />,
              action: onUploadFiles,
            },
        { label: 'Journal', icon: <Edit04Icon />, action: onCreateNote },
        { label: 'Audio', icon: <AudioWavesIcon />, action: onCreateAudio },
        { label: 'Video', icon: <VideoIcon />, action: onCreateVideo },
        ...(onCreateVideoCall
          ? [
              {
                label: 'Video Call',
                icon: <RecordIcon />,
                action: onCreateVideoCall,
              },
            ]
          : []),
      ],
    },
    {
      title: 'Other',
      items: [
        ...(showAlbumSection
          ? [
              {
                label: 'New Album',
                icon: <FolderPlusIcon />,
                action: onCreateAlbum,
              },
              ...(onBulkUpload
                ? [
                    {
                      label: 'Bulk Upload',
                      icon: <FolderPlusIcon />,
                      action: onBulkUpload,
                    },
                  ]
                : []),
            ]
          : []),
        ...(onNewPrompt
          ? [
              {
                label: 'New Prompt',
                icon: <Lightbulb04Icon />,
                action: onNewPrompt,
              },
            ]
          : []),
      ],
    },
  ];
  // "Other" is left out when neither of its items applies (e.g. inside an
  // album for a limited contributor).
  const sections = allSections.filter((section) => section.items.length > 0);

  return (
    <div className={classnames(styles.dropdown, className ?? '')}>
      {sections.map((section) => (
        <div className={styles.section} key={section.title}>
          <span className={styles.sectionHeader}>{section.title}</span>
          {section.items.map((item) => (
            <button
              type="button"
              key={item.label}
              className={styles.item}
              onClick={run(item.action)}
            >
              <span className={styles.itemIcon}>{item.icon}</span>
              <span className={styles.itemLabel}>{item.label}</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
};
